// src/utils/RichTextEditor.js

import React, { useEffect, useState, useRef } from 'react';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import TextAlign from '@tiptap/extension-text-align';
import FontFamily from '@tiptap/extension-font-family';
import ListItem from '@tiptap/extension-list-item';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import {
  Box,
  IconButton,
  Typography,
  Divider,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';

// Icons
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

// --- SHARED COLOR PICKER ---
import ColorPicker from './ColorPicker';

// --- RAW TEXT NORMALIZER ---
const normalizeHtml = (content) => {
  if (!content) return '';
  // If the text already has block-level HTML tags, trust it as HTML
  if (/<\/?(p|br|div|h[1-6]|ul|ol|li|table|blockquote)[^>]*>/i.test(content)) {
    return content;
  }
  // Replace plain text newlines with <br> tags to perfectly match Excel's Alt+Enter spacing
  return String(content).replace(/\r?\n/g, '<br>');
};

// --- ALLOWED LISTS FOR STRICT PARSING (EXPORTED FOR SETTINGS) ---
export const FONT_SIZES = [
  '6pt',
  '8pt',
  '9pt',
  '10pt',
  '11pt',
  '12pt',
  '14pt',
  '18pt',
  '24pt',
  '30pt',
  '36pt',
];

export const ALLOWED_FONTS = [
  'Arial',
  'Comic Sans MS',
  'Courier New',
  'Georgia',
  'Helvetica',
  'Times New Roman',
  'Verdana',
];

// --- 1. CUSTOM FONT SIZE EXTENSION WITH STRICT PARSING ---
const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return { types: ['textStyle', 'listItem'] };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => {
              const size = element.style.fontSize.replace(/['"]+/g, '').trim();
              if (!size) return null;

              // Only allow exact matches from our FONT_SIZES array
              if (FONT_SIZES.includes(size)) return size;
              return null;
            },
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              return { style: `font-size: ${attributes.fontSize}` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain, editor }) => {
          const commands = chain().setMark('textStyle', { fontSize });
          if (editor.isActive('listItem')) {
            commands.updateAttributes('listItem', { fontSize });
          }
          return commands.run();
        },
      unsetFontSize:
        () =>
        ({ chain, editor }) => {
          const commands = chain()
            .setMark('textStyle', { fontSize: null })
            .removeEmptyTextStyle();
          if (editor.isActive('listItem')) {
            commands.updateAttributes('listItem', { fontSize: null });
          }
          return commands.run();
        },
    };
  },
});

// --- 2. CUSTOM FONT FAMILY EXTENSION WITH STRICT PARSING ---
const CustomFontFamily = FontFamily.extend({
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: (element) => {
              const font = element.style.fontFamily.replace(/['"]+/g, '').trim();
              if (!font) return null;

              // Check if the pasted font contains any of our allowed fonts
              const matchedFont = ALLOWED_FONTS.find((f) =>
                font.toLowerCase().includes(f.toLowerCase()),
              );
              return matchedFont || null;
            },
            renderHTML: (attributes) => {
              if (!attributes.fontFamily) return {};
              return { style: `font-family: ${attributes.fontFamily}` };
            },
          },
        },
      },
    ];
  },
});

// --- 3. CUSTOM INDENT EXTENSION ---
const Indent = Extension.create({
  name: 'indent',
  addOptions() {
    return {
      types: ['paragraph', 'heading', 'listItem'],
      minLevel: 0,
      maxLevel: 4,
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: 0,
            parseHTML: (element) => {
              const marginLeft = element.style.marginLeft || '0px';
              return parseInt(marginLeft, 10) / 40 || 0;
            },
            renderHTML: (attributes) => {
              if (!attributes.indent) return {};
              return { style: `margin-left: ${attributes.indent * 40}px` };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      indent:
        () =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          const { from, to } = selection;
          tr.doc.nodesBetween(from, to, (node, pos) => {
            if (this.options.types.includes(node.type.name)) {
              const currentIndent = node.attrs.indent || 0;
              if (currentIndent < this.options.maxLevel) {
                tr.setNodeMarkup(pos, null, { ...node.attrs, indent: currentIndent + 1 });
              }
            }
          });
          return true;
        },
      outdent:
        () =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          const { from, to } = selection;
          tr.doc.nodesBetween(from, to, (node, pos) => {
            if (this.options.types.includes(node.type.name)) {
              const currentIndent = node.attrs.indent || 0;
              if (currentIndent > this.options.minLevel) {
                tr.setNodeMarkup(pos, null, { ...node.attrs, indent: currentIndent - 1 });
              }
            }
          });
          return true;
        },
    };
  },
});

// --- 4. CUSTOM LIST EXTENSIONS ---
const CustomBulletList = BulletList.extend({
  addAttributes() {
    return {
      listStyleType: {
        default: 'disc',
        parseHTML: (element) => element.style.listStyleType,
        renderHTML: (attributes) => ({
          style: `list-style-type: ${attributes.listStyleType};`,
        }),
      },
    };
  },
});

const CustomOrderedList = OrderedList.extend({
  addAttributes() {
    return {
      listStyleType: {
        default: 'decimal',
        parseHTML: (element) => element.style.listStyleType,
        renderHTML: (attributes) => ({
          style: `list-style-type: ${attributes.listStyleType};`,
        }),
      },
    };
  },
});

const EDITOR_EXTENSIONS = [
  StarterKit.configure({
    bulletList: false,
    orderedList: false,
    listItem: false,
    underline: false,
  }),
  ListItem,
  CustomBulletList,
  CustomOrderedList,
  Underline,
  TextStyle,
  Color,
  CustomFontFamily,
  FontSize,
  Indent,
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
];

const RichTextEditor = ({
  initialContent,
  onUpdate,
  readOnly = true,
  readOnlyMessage,
  placeholder,
  defaultFontType = 'Arial',
  defaultFontSize = '11pt',
}) => {
  const [, setUpdateTrigger] = useState(0);
  const isInternalChangeRef = useRef(false);
  const lastInitialContentRef = useRef(initialContent);

  const editor = useEditor({
    extensions: EDITOR_EXTENSIONS,
    content: normalizeHtml(initialContent || ''),
    editable: !readOnly,
    onCreate: ({ editor }) => {
      if (editor.isEmpty) {
        isInternalChangeRef.current = true;
        editor.chain().setFontFamily(defaultFontType).setFontSize(defaultFontSize).run();
        isInternalChangeRef.current = false;
      }
    },
    onUpdate: ({ editor }) => {
      if (isInternalChangeRef.current) return;
      if (editor.isEmpty) {
        isInternalChangeRef.current = true;
        editor.chain().setFontFamily(defaultFontType).setFontSize(defaultFontSize).run();
        isInternalChangeRef.current = false;
      }
      if (onUpdate) onUpdate(editor.getHTML());
    },
    onTransaction: () => {
      setUpdateTrigger((prev) => prev + 1);
    },
  });

  useEffect(() => {
    if (editor && initialContent !== lastInitialContentRef.current) {
      lastInitialContentRef.current = initialContent;
      const normalized = normalizeHtml(initialContent || '');
      if (normalized !== editor.getHTML()) {
        isInternalChangeRef.current = true;
        editor.commands.setContent(normalized, false);
        if (editor.isEmpty) {
          editor
            .chain()
            .setFontFamily(defaultFontType)
            .setFontSize(defaultFontSize)
            .run();
        }
        isInternalChangeRef.current = false;
      }
    }
  }, [initialContent, editor, defaultFontType, defaultFontSize]);

  useEffect(() => {
    if (editor && editor.isEmpty) {
      isInternalChangeRef.current = true;
      editor.chain().setFontFamily(defaultFontType).setFontSize(defaultFontSize).run();
      isInternalChangeRef.current = false;
    }
  }, [defaultFontType, defaultFontSize, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!readOnly);
  }, [readOnly, editor]);

  if (!editor) return null;

  // --- HELPERS ---
  const getEffectiveAttribute = (type, attr) => {
    const attrs = editor.getAttributes(type);
    if (attrs[attr]) return attrs[attr];
    const storedMarks = editor.state.storedMarks || editor.state.selection.$from.marks();
    const textStyle = storedMarks.find((mark) => mark.type.name === 'textStyle');
    return textStyle?.attrs[attr] || null;
  };

  const isFormatActive = (name) => {
    if (editor.isActive(name)) return true;
    const storedMarks = editor.state.storedMarks || editor.state.selection.$from.marks();
    return storedMarks.some((mark) => mark.type.name === name);
  };

  const getCurrentListType = () => {
    if (editor.isActive('bulletList'))
      return editor.getAttributes('bulletList').listStyleType || 'disc';
    if (editor.isActive('orderedList'))
      return editor.getAttributes('orderedList').listStyleType || 'decimal';
    return 'none';
  };

  const handleReset = () => {
    editor
      .chain()
      .focus()
      .unsetBold()
      .unsetItalic()
      .unsetUnderline()
      .clearNodes()
      .unsetAllMarks()
      .setFontFamily(defaultFontType)
      .setFontSize(defaultFontSize)
      .unsetColor()
      .run();
  };

  const handleListChange = (event) => {
    const value = event.target.value;
    if (value === 'none') {
      if (editor.isActive('bulletList')) editor.chain().focus().toggleBulletList().run();
      if (editor.isActive('orderedList'))
        editor.chain().focus().toggleOrderedList().run();
      return;
    }
    const isBullet = value === 'disc' || value === 'circle';
    if (isBullet) {
      if (editor.isActive('bulletList'))
        editor
          .chain()
          .focus()
          .updateAttributes('bulletList', { listStyleType: value })
          .run();
      else
        editor
          .chain()
          .focus()
          .toggleBulletList()
          .updateAttributes('bulletList', { listStyleType: value })
          .run();
    } else {
      if (editor.isActive('orderedList'))
        editor
          .chain()
          .focus()
          .updateAttributes('orderedList', { listStyleType: value })
          .run();
      else
        editor
          .chain()
          .focus()
          .toggleOrderedList()
          .updateAttributes('orderedList', { listStyleType: value })
          .run();
    }
  };

  // Safe checks for MUI Selects to ensure they never throw out-of-range errors
  const currentFontSize =
    getEffectiveAttribute('textStyle', 'fontSize') || defaultFontSize;
  const safeFontSize = FONT_SIZES.includes(currentFontSize)
    ? currentFontSize
    : defaultFontSize;

  const currentFontFamily =
    getEffectiveAttribute('textStyle', 'fontFamily') || defaultFontType;
  const safeFontFamily = ALLOWED_FONTS.includes(currentFontFamily)
    ? currentFontFamily
    : defaultFontType;

  const MenuBtn = ({ active, onClick, children, title }) => (
    <IconButton
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      onMouseDown={(e) => e.preventDefault()}
      sx={{
        color: active ? '#1976d2' : '#555',
        bgcolor: active ? 'rgba(25, 118, 210, 0.15)' : 'transparent',
        borderRadius: 1,
        padding: '3px',
        width: '24px',
        height: '24px',
        margin: '0 1px',
        border: active ? '1px solid #1976d2' : '1px solid transparent',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
      }}
      title={title}>
      {children}
    </IconButton>
  );

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'white',
        position: 'relative',
        zIndex: 99999,
      }}>
      {!readOnly && (
        <Box
          sx={{
            p: 0.5,
            borderBottom: '1px solid #e0e0e0',
            bgcolor: '#f8f9fa',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 0.25,
            textTransform: 'none !important',
          }}>
          <IconButton
            onClick={handleReset}
            title='Reset All Formatting'
            sx={{
              mr: 0.5,
              color: '#d32f2f',
              padding: '3px',
              width: '24px',
              height: '24px',
            }}>
            <RestartAltIcon style={{ fontSize: '15px' }} />
          </IconButton>

          <Divider orientation='vertical' flexItem sx={{ mx: 0.5, height: 16 }} />

          <FormControl size='small' variant='standard' sx={{ minWidth: 90, mr: 0.5 }}>
            <Select
              value={safeFontFamily}
              onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
              disableUnderline
              renderValue={(val) => (
                <span style={{ textTransform: 'none', display: 'block' }}>{val}</span>
              )}
              MenuProps={{ sx: { zIndex: 100009 } }}
              sx={{ fontSize: '12px', '& .MuiSelect-select': { py: 0 } }}>
              {ALLOWED_FONTS.map((f) => (
                <MenuItem key={f} value={f} sx={{ fontFamily: f, fontSize: '12px' }}>
                  <span style={{ textTransform: 'none', display: 'block' }}>{f}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size='small' variant='standard' sx={{ minWidth: 50, mr: 0.5 }}>
            <Select
              value={safeFontSize}
              onChange={(e) => editor.chain().focus().setFontSize(e.target.value).run()}
              disableUnderline
              renderValue={(val) => (
                <span style={{ textTransform: 'none', display: 'block' }}>{val}</span>
              )}
              MenuProps={{ sx: { zIndex: 100009 } }}
              sx={{ fontSize: '12px', '& .MuiSelect-select': { py: 0 } }}>
              {FONT_SIZES.map((s) => (
                <MenuItem key={s} value={s} sx={{ fontSize: '12px' }}>
                  <span style={{ textTransform: 'none', display: 'block' }}>{s}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider orientation='vertical' flexItem sx={{ mx: 0.5, height: 16 }} />

          <MenuBtn
            active={isFormatActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}>
            <FormatBoldIcon style={{ fontSize: '15px' }} />
          </MenuBtn>
          <MenuBtn
            active={isFormatActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}>
            <FormatItalicIcon style={{ fontSize: '15px' }} />
          </MenuBtn>
          <MenuBtn
            active={isFormatActive('underline')}
            onClick={() => editor.chain().focus().toggleUnderline().run()}>
            <FormatUnderlinedIcon style={{ fontSize: '15px' }} />
          </MenuBtn>

          <Divider orientation='vertical' flexItem sx={{ mx: 0.5, height: 16 }} />

          <FormControl size='small' variant='standard' sx={{ mr: 0.5 }}>
            <Select
              value={getCurrentListType()}
              onChange={handleListChange}
              disableUnderline
              displayEmpty
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {selected === 'disc' ? (
                    <FiberManualRecordIcon
                      style={{ fontSize: '13.5px', color: '#1976d2' }}
                    />
                  ) : selected === 'circle' ? (
                    <RadioButtonUncheckedIcon
                      style={{ fontSize: '13.5px', color: '#1976d2' }}
                    />
                  ) : selected === 'decimal' ? (
                    <FormatListNumberedIcon
                      style={{ fontSize: '15px', color: '#1976d2' }}
                    />
                  ) : (
                    <FormatListBulletedIcon
                      style={{
                        fontSize: '15px',
                        color: selected === 'none' ? '#777' : '#1976d2',
                      }}
                    />
                  )}
                  <ArrowDropDownIcon
                    style={{ fontSize: '15px', color: '#777', marginLeft: '-4px' }}
                  />
                </Box>
              )}
              MenuProps={{ sx: { zIndex: 100009 } }}
              IconComponent={() => null}
              sx={{
                '& .MuiSelect-select': {
                  py: 0,
                  pr: '0 !important',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}>
              <MenuItem value='none' sx={{ fontSize: '12px' }}>
                <span style={{ textTransform: 'none', display: 'block' }}>None</span>
              </MenuItem>
              <MenuItem
                value='disc'
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}>
                <FiberManualRecordIcon style={{ fontSize: '11px' }} />
              </MenuItem>
              <MenuItem
                value='circle'
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                }}>
                <RadioButtonUncheckedIcon style={{ fontSize: '11px' }} />
              </MenuItem>
              <MenuItem value='decimal' sx={{ fontSize: '12px' }}>
                <span style={{ textTransform: 'none', display: 'block' }}>1. Number</span>
              </MenuItem>
            </Select>
          </FormControl>

          <MenuBtn onClick={() => editor.chain().focus().outdent().run()}>
            <FormatIndentDecreaseIcon style={{ fontSize: '15px' }} />
          </MenuBtn>
          <MenuBtn onClick={() => editor.chain().focus().indent().run()}>
            <FormatIndentIncreaseIcon style={{ fontSize: '15px' }} />
          </MenuBtn>

          <Divider orientation='vertical' flexItem sx={{ mx: 0.5, height: 16 }} />

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ColorPicker
              value={getEffectiveAttribute('textStyle', 'color') || '#000000'}
              onChange={(color) => editor.chain().focus().setColor(color).run()}
              label='Text Color'
              sx={{
                width: 18,
                height: 18,
                padding: '2px',
                border: '1.5px solid #1976d2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          </Box>

          <Divider orientation='vertical' flexItem sx={{ mx: 0.5, height: 16 }} />

          <MenuBtn onClick={() => editor.chain().focus().undo().run()}>
            <UndoIcon style={{ fontSize: '15px' }} />
          </MenuBtn>
          <MenuBtn onClick={() => editor.chain().focus().redo().run()}>
            <RedoIcon style={{ fontSize: '15px' }} />
          </MenuBtn>
        </Box>
      )}

      <Box
        onClick={() => {
          if (editor && !readOnly) {
            editor.commands.focus();
          }
        }}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          position: 'relative',
          cursor: 'text',
          display: 'flex',
          flexDirection: 'column',
          '& > div': {
            display: 'flex',
            flexGrow: 1,
            flexDirection: 'column',
          },
          '& .ProseMirror': {
            flexGrow: 1,
            padding: '16px',
            outline: 'none',
            minHeight: '100px',
            fontFamily: `${defaultFontType}, Arial, sans-serif`,
            fontSize: defaultFontSize,
            lineHeight: '1.2',
            textTransform: 'none !important',
          },
          // STRICT 6pt MARGINS EVERYWHERE
          '& .ProseMirror > *:first-of-type': { marginTop: 0 },
          '& .ProseMirror p': { marginTop: 0, marginBottom: '6pt', lineHeight: '1.2' },
          // List container margin set to 0 so LI spacing controls the gaps
          '& .ProseMirror ul, & .ProseMirror ol': { paddingLeft: '1.5rem', margin: 0 },
          // List Item margin controls spacing
          '& .ProseMirror li': { marginTop: '0', marginBottom: '6pt', lineHeight: '1.1' },
          '& .ProseMirror li > p': { margin: '0 !important' },
          '& .ProseMirror li > *': { fontSize: defaultFontSize },
        }}>
        {!readOnly && placeholder && editor.isEmpty && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              pointerEvents: 'none',
              color: 'text.secondary',
              zIndex: 1,
              fontStyle: 'italic',
              textTransform: 'none !important',
            }}>
            {placeholder}
          </Box>
        )}

        {readOnly && !initialContent ? (
          <Typography
            sx={{
              padding: '16px',
              fontStyle: 'italic',
              color: 'text.secondary',
              fontSize: '14px',
              textTransform: 'none !important',
            }}>
            {readOnlyMessage || 'No photo selected.'}
          </Typography>
        ) : (
          <EditorContent
            editor={editor}
            style={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}
          />
        )}
      </Box>
    </Box>
  );
};

export default RichTextEditor;
